import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    X, ChevronDown, ChevronRight, ChevronUp, Plus, Video, Image as ImageIcon, Award, Smile,
    Megaphone, Clock, Edit3, Send, Trash2, Newspaper, Check, ArrowLeft, Trophy, Search,
} from 'lucide-react';
import { Dropdown, MenuProps, Modal, Spin, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'hooks/useDebounce';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';

import { PostCard, ShareModal } from './component';
import { BirthdayWidget, wishHtml } from './rightPanel/BirthdayWidget';
import { MeetingModal, ScheduleWidget } from './rightPanel/ScheduleWidget';
import { QuickLinksWidget } from './rightPanel/QuickLinksWidget';
import { COLLEAGUES, upcomingOccasions, useWishesSent, type Occasion } from './rightPanel/rightPanelData';
import CreatePostModal, { loadDraft, type ComposerMode } from './component/CreatePostModal';
import { UserAvatar } from './component/UserAvatar';
import Layout from '../../Layout';
import UserSidebar, { USER_PAGE_ROW } from '../../components/SidebarNew/UserSidebar';
import {
    badgeOf, htmlToText, prefsOf, socialApi, useSocialState,
    type FeedFilter, type Post,
} from './socialStore';
import { feedApi, type LeaderRow } from './socialApi';
import { useCounters, useFeed, useRecognitions, useScheduled, useSocialMe } from './useFeed';

const FILTERS: { key: FeedFilter; label: string }[] = [
    { key: 'all', label: 'Tất cả bài viết' },
    { key: 'mine', label: 'Bài viết của tôi' },
    { key: 'saved', label: 'Bài viết đã lưu' },
    { key: 'recognition', label: 'Ghi nhận' },
    { key: 'mentions', label: 'Nhắc đến tôi' },
];

const EMPTY_TEXT: Record<FeedFilter, string> = {
    all: 'Chưa có bài viết nào. Hãy là người đầu tiên chia sẻ!',
    mine: 'Bạn chưa đăng bài viết nào.',
    saved: 'Bạn chưa lưu bài viết nào. Chọn “Lưu bài viết” trong menu ⋮ của bài để xem lại sau.',
    recognition: 'Chưa có lời ghi nhận nào. Hãy ghi nhận một đồng nghiệp!',
    mentions: 'Chưa có bài viết hay bình luận nào nhắc đến bạn.',
};

// ---------------------------------------------------------------------------
// Cột phải: sinh nhật & kỷ niệm, tạo cuộc họp, lịch làm việc, truy cập nhanh
// ("Thông báo hệ thống" nằm ở sidebar trái nên cột phải không lặp lại thông báo)
// ---------------------------------------------------------------------------
const RightSidebar = ({ meId, onWish, onCreateMeeting }: { meId: string; onWish: (o: Occasion) => void; onCreateMeeting: () => void }) => (
    <div className="hidden lg:flex lg:w-80 flex-shrink-0 flex-col space-y-4 self-start lg:sticky lg:top-4 lg:overflow-y-auto">
        <BirthdayWidget meId={meId} onWish={onWish} />

        <button onClick={onCreateMeeting} className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition group cursor-pointer">
            <div className="flex items-center">
                <div className="w-8 h-8 rounded-lg bg-green-500 text-white flex items-center justify-center mr-3">
                    <Video className="w-4 h-4" />
                </div>
                <span className="font-bold text-gray-800 dark:text-gray-200 text-sm">Tạo cuộc họp</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
        </button>

        <ScheduleWidget meId={meId} onCreate={onCreateMeeting} />

        <QuickLinksWidget meId={meId} />

        {/* Lối tắt nhóm: tạm ẩn vì chưa có tính năng nhóm thật (component GroupShortcuts vẫn giữ để bật lại) */}
    </div>
);

// ---------------------------------------------------------------------------
// Nhắc bật thông báo trình duyệt — chỉ hiện khi chưa cấp/chặn quyền và chưa bấm "Bỏ qua"
// ---------------------------------------------------------------------------
const NotificationPrompt = ({ onClose }: { onClose: () => void }) => {
    const enable = () => {
        Notification.requestPermission().then(p => {
            if (p === 'granted') message.success('Đã bật thông báo trên trình duyệt');
            else if (p === 'denied') message.info('Bạn đã chặn thông báo. Có thể bật lại trong cài đặt trang của trình duyệt.');
            onClose();
        }).catch(onClose);
    };
    return (
        <div className="fixed bottom-6 right-6 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 z-40">
            <div className="p-5 flex items-start">
                <div className="relative mr-4 flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gray-800 dark:bg-gray-700 text-white flex items-center justify-center"><Megaphone className="w-5 h-5" /></div>
                    <div className="absolute top-0 -right-1 w-3.5 h-3.5 bg-red-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
                </div>
                <p className="flex-1 text-sm font-bold text-gray-900 dark:text-gray-100">Bật thông báo trên trình duyệt để không bỏ lỡ bài viết và bình luận mới</p>
                <button onClick={onClose} aria-label="Đóng" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 pb-5 flex gap-3">
                <button onClick={onClose} className="flex-1 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-bold rounded-lg text-xs cursor-pointer">Bỏ qua</button>
                <button onClick={enable} className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg text-xs shadow-sm cursor-pointer">Bật thông báo</button>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Bài viết hẹn giờ của tôi
// ---------------------------------------------------------------------------
const ScheduledPanel = ({ posts, count, onEdit, onPublish, onDelete }: { posts: Post[]; count: number; onEdit: (p: Post) => void; onPublish: (p: Post) => void; onDelete: (p: Post) => void }) => {
    const [open, setOpen] = useState(false);
    if (!posts.length) return null;
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-cyan-200 dark:border-cyan-800 overflow-hidden">
            <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center justify-between text-left cursor-pointer hover:bg-cyan-50/50 dark:hover:bg-cyan-900/10">
                <span className="flex items-center gap-2 font-bold text-gray-900 dark:text-gray-100">
                    <span className="w-8 h-8 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 flex items-center justify-center"><Clock className="w-4 h-4" /></span>
                    Bài viết hẹn giờ <span className="text-xs font-bold bg-cyan-600 text-white rounded-full px-2 py-0.5">{count || posts.length}</span>
                </span>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                    Đăng sớm nhất {dayjs(posts[0].publishAt).format('HH:mm DD/MM')}
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
            </button>
            {open && (
                <div className="divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700">
                    {posts.map(p => {
                        const text = p.title || htmlToText(p.html) || (p.kind === 'recognition' ? `Ghi nhận ${p.recognition?.people.map(x => x.name).join(', ')}` : `${p.images.length} ảnh`);
                        return (
                            <div key={p.id} className="px-4 py-3 flex items-center gap-3">
                                <div className="text-center flex-shrink-0 w-14">
                                    <div className="text-sm font-bold text-cyan-700 dark:text-cyan-400">{dayjs(p.publishAt).format('HH:mm')}</div>
                                    <div className="text-[11px] text-gray-500">{dayjs(p.publishAt).format('DD/MM')}</div>
                                </div>
                                <div className="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-200 truncate">{text}</div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <button title="Sửa" onClick={() => onEdit(p)} className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 cursor-pointer"><Edit3 className="w-4 h-4" /></button>
                                    <button title="Đăng ngay" onClick={() => onPublish(p)} className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-green-600 cursor-pointer"><Send className="w-4 h-4" /></button>
                                    <button title="Xóa" onClick={() => onDelete(p)} className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-red-500 cursor-pointer"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Ghi nhận mới nhất + bảng xếp hạng
// ---------------------------------------------------------------------------
const LatestRecognition = ({ post, now, onOpen, onBoard, onCreate }: { post?: Post; now: number; onOpen: (id: string) => void; onBoard: () => void; onCreate: () => void }) => {
    const r = post?.recognition;
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 flex justify-between items-center border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center"><span className="text-lg">🏆</span></div>
                    <span className="font-bold text-gray-900 dark:text-gray-100">Ghi nhận mới nhất</span>
                </div>
                <button onClick={onBoard} className="text-green-600 hover:text-green-700 text-[13px] font-medium flex items-center cursor-pointer">
                    Chi tiết & BXH <ChevronRight className="w-3 h-3 ml-1" />
                </button>
            </div>
            <div className="p-4">
                {post && r ? (
                    <button onClick={() => onOpen(post.id)} className="w-full text-left border border-gray-100 dark:border-gray-700 rounded-xl p-3 flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer shadow-sm">
                        <div className="w-16 h-16 md:w-12 md:h-12 flex-shrink-0 relative flex items-center justify-center">
                            <svg viewBox="0 0 100 100" className="w-full h-full text-orange-100 dark:text-orange-900 absolute inset-0"><polygon fill="currentColor" points="50 3 93 25 93 75 50 97 7 75 7 25" /></svg>
                            <span className="relative z-10 text-2xl">{badgeOf(r.badge).emoji}</span>
                        </div>
                        <div className="flex-1 text-center md:text-left">
                            <p className="text-sm md:text-base text-gray-800 dark:text-gray-300">
                                <span className="font-bold text-gray-900 dark:text-gray-100">{post.authorName}</span> đã ghi nhận{' '}
                                <span className="font-bold text-gray-900 dark:text-gray-100">{r.people.map(p => p.name).join(', ')}</span>
                            </p>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 font-medium">
                                {badgeOf(r.badge).label} · {dayjs(post.publishAt).format('DD/MM/YYYY')}
                            </div>
                        </div>
                    </button>
                ) : (
                    <div className="text-sm text-gray-500 text-center py-2">Chưa có lời ghi nhận nào.</div>
                )}
                <button onClick={onCreate} className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-sm font-bold hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer">
                    <Award className="w-4 h-4" /> Ghi nhận một đồng nghiệp
                </button>
            </div>
        </div>
    );
};

const RecognitionBoard = ({ board }: { board: LeaderRow[] }) => {
    if (!board.length) return null;
    const medal = ['🥇', '🥈', '🥉'];
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 font-bold text-gray-900 dark:text-gray-100 mb-3"><Trophy className="w-4 h-4 text-amber-500" /> Bảng xếp hạng được ghi nhận nhiều nhất</div>
            <div className="space-y-2">
                {board.map(p => (
                    <div key={p.staffId} className="flex items-center gap-3">
                        <span className="w-6 text-center text-lg">{medal[p.rank - 1] || <span className="text-sm text-gray-400 font-bold">{p.rank}</span>}</span>
                        <UserAvatar name={p.name} src={p.avatar} size={32} />
                        <span className="flex-1 text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{p.name}</span>
                        <span className="text-sm font-bold text-amber-600">{p.count} lượt</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Trang Mạng nội bộ
// ---------------------------------------------------------------------------
interface ComposerState { open: boolean; mode: ComposerMode; editing: Post | null; pickImages: boolean; wish?: Occasion }
const CLOSED: ComposerState = { open: false, mode: 'post', editing: null, pickImages: false };

export default function Social() {
    // kho localStorage chỉ còn giữ cờ "đã bỏ qua nhắc bật thông báo"
    const state = useSocialState();
    const me = useSocialMe();
    const prefs = prefsOf(state, me.id);
    const [tick, setTick] = useState(0);
    const [params, setParams] = useSearchParams();
    const [composer, setComposer] = useState<ComposerState>(CLOSED);
    const [sharing, setSharing] = useState<Post | null>(null);
    const [meetingOpen, setMeetingOpen] = useState(false);
    const { markSent: markWishSent } = useWishesSent(me.id);
    // ?wish=<id>:<birthday|anniversary> (từ trang khác) → mở sẵn bài chúc mừng
    const wishParam = params.get('wish');
    useEffect(() => {
        if (!wishParam) return;
        const [pid, kind] = wishParam.split(':');
        const o = upcomingOccasions(COLLEAGUES, dayjs(), 0).find(x => x.person.id === pid && x.kind === kind);
        if (o) setComposer({ open: true, mode: 'post', editing: null, pickImages: false, wish: o });
        const next = new URLSearchParams(params);
        next.delete('wish');
        setParams(next, { replace: true });
    }, [wishParam]); // eslint-disable-line react-hooks/exhaustive-deps

    const rawFilter = params.get('filter') as FeedFilter;
    const filter: FeedFilter = FILTERS.some(f => f.key === rawFilter) ? rawFilter : 'all';
    const focusId = params.get('post');
    // tìm kiếm: gõ đến đâu lọc đến đó (trễ 250ms), từ khoá giữ trên URL để chia sẻ / quay lại được
    // API nhận từ khoá tối đa 200 ký tự (link cũ / dán dài quá thì cắt bớt)
    const q = (params.get('q') || '').slice(0, 200);
    const [qInput, setQInput] = useState(q);
    const qDebounced = useDebounce(qInput, 250);
    useEffect(() => { setQInput(q); }, [q]);
    useEffect(() => {
        if (qDebounced.trim() === q) return;
        const next = new URLSearchParams(params);
        if (qDebounced.trim()) next.set('q', qDebounced.trim()); else next.delete('q');
        next.delete('post');
        setParams(next, { replace: true });
    }, [qDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

    // cập nhật chữ "x phút" mỗi 30 giây (bài hẹn giờ do API tự lọc)
    useEffect(() => {
        const t = setInterval(() => setTick(x => x + 1), 30_000);
        return () => clearInterval(t);
    }, []);

    const feed = useFeed(filter, q);
    const counters = useCounters();
    const now = useMemo(() => Date.now(), [tick, feed.posts]); // eslint-disable-line react-hooks/exhaustive-deps
    const scheduled = useScheduled();
    const recognitions = useRecognitions();

    // Bài mở qua link ?post=: undefined = đang tải, null = không tồn tại / không xem được
    const [focused, setFocused] = useState<Post | null | undefined>(undefined);
    useEffect(() => {
        if (!focusId) return;
        let alive = true;
        setFocused(undefined);
        feedApi.get(focusId).then(p => { if (alive) setFocused(p); }).catch(() => { if (alive) setFocused(null); });
        return () => { alive = false; };
    }, [focusId]);

    // bài vừa ẩn trong phiên này vẫn giữ chỗ (thẻ thu gọn) để bấm "Hoàn tác"; tải lại thì API đã loại bài đó
    const [hiddenNow, setHiddenNow] = useState<string[]>([]);
    const viewed = useRef(new Set<string>());

    const patchPost = (id: string, fn: (p: Post) => Post) => {
        feed.patch(id, fn);
        setFocused(f => (f && f.id === id ? fn(f) : f));
    };
    const toggleSave = async (p: Post) => {
        try {
            const r = await feedApi.setSaved(p.id, !p.viewer?.saved);
            patchPost(p.id, x => ({ ...x, viewer: x.viewer && { ...x.viewer, saved: r.saved } }));
            // đang ở "Đã lưu" thì bài vừa bỏ lưu rời khỏi danh sách
            if (filter === 'saved' && !r.saved) feed.remove(p.id);
            message.success(r.message);
        } catch (e: any) { message.error(e.message); }
    };
    const toggleHide = async (p: Post) => {
        const hide = !hiddenNow.includes(p.id);
        try {
            await feedApi.setHidden(p.id, hide);
            setHiddenNow(list => (hide ? [...list, p.id] : list.filter(x => x !== p.id)));
            counters.refresh();
        } catch (e: any) { message.error(e.message); }
    };
    const markViewed = (id: string) => {
        if (viewed.current.has(id)) return;
        viewed.current.add(id);
        feedApi.view(id).then(r => { if (r.counted) patchPost(id, x => ({ ...x, views: r.views })); }).catch(() => viewed.current.delete(id));
    };
    const onPosted = (p: Post) => {
        if ((filter === 'all' || filter === 'mine') && !q) feed.prepend(p); else feed.reload();
        counters.refresh();
    };

    // Sửa / đăng bài từ hộp soạn: bài hẹn giờ vào khối riêng, bài đã đăng cập nhật tại chỗ, bài mới lên đầu bảng tin
    const onSaved = (p: Post) => {
        const editing = composer.editing;
        if (p.kind === 'recognition') recognitions.reload();
        if (p.scheduled || editing?.scheduled) scheduled.reload();
        if (p.scheduled || (editing && !editing.scheduled)) {
            if (!p.scheduled) patchPost(p.id, () => p);
            counters.refresh();
            return;
        }
        onPosted(p);
    };
    const publishNow = async (p: Post) => {
        try {
            const r = await feedApi.publish(p.id);
            scheduled.remove(p.id);
            if (p.kind === 'recognition') recognitions.reload();
            onPosted(r.post);
            message.success(r.message);
        } catch (e: any) {
            message.error(e.message);
            // bài đã tới giờ hoặc vừa đăng ở tab khác
            if (e.status === 409) { scheduled.reload(); feed.reload(); recognitions.reload(); }
            counters.refresh();
        }
    };
    const deletePost = (p: Post) => Modal.confirm({
        title: p.scheduled ? 'Xóa bài viết hẹn giờ?' : 'Xóa bài viết?', okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy',
        onOk: async () => {
            try {
                const msg = await feedApi.remove(p.id);
                feed.remove(p.id);
                scheduled.remove(p.id);
                if (focusId === p.id) closeFocus();
                if (p.kind === 'recognition') recognitions.reload();
                message.success(msg);
                counters.refresh();
            } catch (e: any) { message.error(e.message); }
        },
    });
    const toggleLock = async (p: Post) => {
        try {
            const updated = await feedApi.setCommentsLocked(p.id, !p.commentsLocked);
            patchPost(p.id, () => updated);
            message.success(updated.commentsLocked ? 'Đã chặn bình luận' : 'Đã mở lại bình luận');
        } catch (e: any) { message.error(e.message); }
    };

    // Bài hẹn giờ tới giờ thì API tự đưa lên bảng tin: tải lại cả hai danh sách
    useEffect(() => {
        if (!scheduled.posts.some(p => new Date(p.publishAt).getTime() <= Date.now())) return;
        scheduled.reload();
        feed.reload();
        counters.refresh();
        recognitions.reload();
        message.info('Bài viết hẹn giờ đã được đăng');
    }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

    const byId = useMemo(() => new Map(feed.posts.map(p => [p.id, p])), [feed.posts]);
    const mentionCount = counters.mentions;

    const draft = composer.open ? null : loadDraft(me.id, 'post');
    const draftText = draft ? (draft.title || htmlToText(draft.content)).slice(0, 60) : '';

    const list = focusId ? feed.posts.filter(p => p.id !== focusId) : feed.posts;

    const setFilter = (key: FeedFilter) => {
        const next = new URLSearchParams(params);
        if (key === 'all') next.delete('filter'); else next.set('filter', key);
        next.delete('post');
        setParams(next);
    };
    const openPost = (id: string) => {
        const next = new URLSearchParams(params);
        next.set('post', id);
        setParams(next);
    };
    const closeFocus = () => {
        const next = new URLSearchParams(params);
        next.delete('post');
        setParams(next);
    };

    const openComposer = (mode: ComposerMode, pickImages = false) => setComposer({ open: true, mode, editing: null, pickImages });
    const editPost = (p: Post) => setComposer({ open: true, mode: 'post', editing: p, pickImages: false });

    const showNotiPrompt = typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default' && !prefs.notiPromptDismissed;

    const filterItems: MenuProps['items'] = FILTERS.map(f => ({
        key: f.key,
        label: <span className="flex items-center justify-between gap-6"><span>{f.label}{f.key === 'mentions' && mentionCount > 0 && <span className="ml-1.5 text-[11px] font-bold bg-red-500 text-white rounded-full px-1.5">{mentionCount}</span>}</span>{filter === f.key && <Check className="w-4 h-4 text-green-600" />}</span>,
        onClick: () => setFilter(f.key),
    }));

    const card = (p: Post, highlighted = false) => (
        <PostCard
            key={p.id} post={p} me={me} now={now} hidden={hiddenNow.includes(p.id)} findPost={id => byId.get(id)}
            onShare={setSharing} onEdit={editPost} onToggleSave={toggleSave} onToggleHide={toggleHide} onToggleLock={toggleLock} onDelete={deletePost} onViewed={markViewed} highlighted={highlighted}
        />
    );
    const pickImages = () => openComposer('post', true);
    const recognize = () => openComposer('recognition');

    return (
        <Layout>
            <Helmet>
                <title>Mạng nội bộ | Khothi</title>
            </Helmet>
            <div className="flex min-h-screen bg-brand-gray dark:bg-gray-900 text-gray-800 dark:text-gray-200">
                <div className="flex-1 flex flex-col relative">
                    <main className="flex-1 bg-brand-gray dark:bg-gray-900">
                        <div className={USER_PAGE_ROW}>

                            {/* Left Sidebar */}
                            <UserSidebar />

                            {/* Main Feed */}
                            <div className="flex-1 flex flex-col space-y-4 min-w-0 w-full max-w-[760px] mx-auto">
                                {/* Post Composer */}
                                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                                    <button onClick={() => openComposer('post')} className="w-full p-3 md:p-4 flex items-center space-x-3 text-left cursor-pointer">
                                        <UserAvatar name={me.name} src={me.avatar} />
                                        <span className="flex-1 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full px-4 py-2.5 text-gray-400 dark:text-gray-400 text-base font-medium transition">
                                            {draftText ? <span className="text-gray-600 dark:text-gray-300">📝 Viết tiếp bản nháp: “{draftText}”</span> : 'Bạn muốn chia sẻ điều gì?'}
                                        </span>
                                    </button>
                                    <div className="border-t border-gray-100 dark:border-gray-700"></div>
                                    <div className="px-2 md:px-3 py-1.5 flex items-center justify-between gap-1">
                                        <div className="flex items-center">
                                            <button onClick={pickImages} className="flex items-center text-gray-600 dark:text-gray-400 font-bold text-sm px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                                                <ImageIcon className="text-blue-500 w-5 h-5 mr-1.5" /> Ảnh
                                            </button>
                                            <button onClick={recognize} className="flex items-center text-gray-600 dark:text-gray-400 font-bold text-sm px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                                                <Award className="text-yellow-500 w-5 h-5 mr-1.5" /> Ghi nhận
                                            </button>
                                        </div>
                                        <button onClick={() => openComposer('post')} title="Cảm xúc" className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                                            <Smile className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <ScheduledPanel posts={scheduled.posts} count={counters.scheduled} onEdit={editPost} onPublish={publishNow} onDelete={deletePost} />

                                {focusId ? (
                                    <>
                                        <button onClick={closeFocus} className="self-start flex items-center gap-1.5 text-sm font-bold text-green-600 hover:underline cursor-pointer">
                                            <ArrowLeft className="w-4 h-4" /> Về bảng tin
                                        </button>
                                        {focused === undefined ? (
                                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 flex justify-center"><Spin /></div>
                                        ) : focused ? card(focused, true) : (
                                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center text-sm text-gray-500">
                                                Bài viết không tồn tại hoặc đã bị xóa.
                                            </div>
                                        )}
                                        <div className="text-xs font-bold text-gray-500 uppercase tracking-wider px-1 pt-2">Bài viết khác</div>
                                    </>
                                ) : (
                                    <>
                                        <div className="relative">
                                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                            <input
                                                type="search"
                                                value={qInput}
                                                onChange={e => setQInput(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Escape') setQInput(''); }}
                                                maxLength={200}
                                                placeholder="Tìm bài viết theo nội dung, người đăng, người được nhắc..."
                                                aria-label="Tìm bài viết"
                                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl pl-9 pr-9 py-2.5 text-sm text-gray-800 dark:text-gray-200 outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 [&::-webkit-search-cancel-button]:hidden"
                                            />
                                            {qInput && (
                                                <button onClick={() => setQInput('')} aria-label="Xóa từ khóa" className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-400 cursor-pointer">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center py-1 px-1">
                                            <span className="font-bold text-gray-800 text-[13px] dark:text-gray-400">{q ? (feed.loading && !list.length ? <>Đang tìm “{q}”...</> : feed.error ? <>Tìm “{q}”</> : <>{list.length}{feed.hasMore ? '+' : ''} kết quả cho “{q}”</>) : 'Hiển thị'}</span>
                                            <Dropdown menu={{ items: filterItems, selectedKeys: [filter] }} trigger={['click']} placement="bottomRight">
                                                <button className="text-green-600 font-bold text-[13px] hover:bg-green-50 dark:hover:bg-green-900/20 px-2 py-1.5 rounded transition flex items-center cursor-pointer">
                                                    {FILTERS.find(f => f.key === filter)?.label} <ChevronDown className="w-3 h-3 ml-1.5" />
                                                </button>
                                            </Dropdown>
                                        </div>
                                        {!q && (filter === 'recognition'
                                            ? <RecognitionBoard board={recognitions.board} />
                                            : filter === 'all' && <LatestRecognition post={recognitions.latest ?? undefined} now={now} onOpen={openPost} onBoard={() => setFilter('recognition')} onCreate={recognize} />)}
                                    </>
                                )}

                                {list.map(p => card(p))}

                                {feed.loading && !list.length && (
                                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 flex justify-center"><Spin /></div>
                                )}

                                {feed.error && !list.length && (
                                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
                                        <p className="text-sm text-red-500">{feed.error}</p>
                                        <button onClick={feed.reload} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">Thử lại</button>
                                    </div>
                                )}

                                {list.length === 0 && !focusId && !feed.loading && !feed.error && (
                                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
                                        <Newspaper className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                        <p className="text-sm text-gray-500 dark:text-gray-400">{q ? <>Không tìm thấy bài viết nào khớp “{q}”{filter !== 'all' && <> trong mục “{FILTERS.find(f => f.key === filter)?.label}”</>}.</> : EMPTY_TEXT[filter]}</p>
                                        {q ? (
                                            <button onClick={() => setQInput('')} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">Xóa từ khóa</button>
                                        ) : filter !== 'saved' && filter !== 'mentions' && (
                                            <button onClick={() => (filter === 'recognition' ? recognize() : openComposer('post'))} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-bold cursor-pointer">
                                                <Plus className="w-4 h-4" /> {filter === 'recognition' ? 'Ghi nhận đồng nghiệp' : 'Tạo bài viết'}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {feed.hasMore && list.length > 0 && (
                                    <button onClick={feed.loadMore} disabled={feed.loading} className="w-full py-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 cursor-pointer">
                                        {feed.loading ? 'Đang tải...' : 'Xem thêm bài viết'}
                                    </button>
                                )}
                                {!feed.hasMore && list.length > 3 && (
                                    <div className="text-center text-xs text-gray-400 py-2">Bạn đã xem hết bài viết</div>
                                )}
                            </div>

                            {/* Right Sidebar */}
                            <RightSidebar meId={me.id} onWish={o => setComposer({ open: true, mode: 'post', editing: null, pickImages: false, wish: o })} onCreateMeeting={() => setMeetingOpen(true)} />
                        </div>

                        {showNotiPrompt && <NotificationPrompt onClose={() => socialApi.dismissNotiPrompt(me.id)} />}
                    </main>
                </div>

                <ShareModal post={sharing} me={me} closeShare={() => setSharing(null)} />
                <CreatePostModal
                    open={composer.open}
                    mode={composer.mode}
                    editing={composer.editing}
                    pickImages={composer.pickImages}
                    initialHtml={composer.wish ? wishHtml(composer.wish) : undefined}
                    onPosted={p => { if (composer.wish) markWishSent(composer.wish); onSaved(p); }}
                    me={me}
                    onClose={() => setComposer(CLOSED)}
                />
                <MeetingModal open={meetingOpen} meId={me.id} onClose={() => setMeetingOpen(false)} />
            </div>
        </Layout>
    );
}
